import { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useParams, useNavigate } from "react-router-dom";
import {
  GET_WITHDRAWAL_DETAIL,
  APPROVE_WITHDRAWAL,
  REJECT_WITHDRAWAL,
  MARK_WITHDRAWAL_PAID,
} from "../../api/review";
// Note: GET_USER_WALLET_LEDGERS는 현재 로그인한 유저의 지갑 내역만 조회 가능
// 출금 요청 검수에서는 특정 유저의 지갑 내역이 필요하지만,
// 현재 GraphQL 스키마에 해당 쿼리가 없으므로 일단 제외
// 필요시 백엔드에 missionAdminUserWalletLedgers 쿼리 추가 필요

const WithdrawalDetail = () => {
  const { withdrawalRequestId } = useParams<{ withdrawalRequestId: string }>();
  const navigate = useNavigate();
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [externalTransferRef, setExternalTransferRef] = useState("");

  const { data, loading, error, refetch } = useQuery(GET_WITHDRAWAL_DETAIL, {
    variables: { withdrawalRequestId: parseInt(withdrawalRequestId || "0") },
  });

  // TODO: 특정 유저의 지갑 내역 조회 쿼리 추가 필요
  // const { data: ledgersData } = useQuery(GET_USER_WALLET_LEDGERS, {
  //   variables: { userId: data?.missionAdminWithdrawal?.userId },
  //   skip: !data?.missionAdminWithdrawal?.userId,
  // });
  const ledgersData = { userWalletLedgers: [] };

  const [approveWithdrawal, { loading: approving }] = useMutation(
    APPROVE_WITHDRAWAL,
    {
      onCompleted: () => {
        alert("승인되었습니다.");
        refetch();
      },
      onError: (error) => {
        alert(`오류: ${error.message}`);
      },
    }
  );

  const [rejectWithdrawal, { loading: rejecting }] = useMutation(
    REJECT_WITHDRAWAL,
    {
      onCompleted: () => {
        alert("반려되었습니다.");
        setShowRejectModal(false);
        setRejectReason("");
        refetch();
      },
      onError: (error) => {
        alert(`오류: ${error.message}`);
      },
    }
  );

  const [markWithdrawalPaid, { loading: markingPaid }] = useMutation(
    MARK_WITHDRAWAL_PAID,
    {
      onCompleted: () => {
        alert("이체 완료 처리되었습니다.");
        setShowPaymentModal(false);
        setExternalTransferRef("");
        refetch();
      },
      onError: (error) => {
        alert(`오류: ${error.message}`);
      },
    }
  );

  const handleApprove = () => {
    if (!window.confirm("이 출금 요청을 승인하시겠습니까?")) {
      return;
    }

    approveWithdrawal({
      variables: {
        withdrawalRequestId: parseInt(withdrawalRequestId || "0"),
      },
    });
  };

  const handleReject = () => {
    if (!rejectReason.trim()) {
      alert("반려 사유를 입력해주세요.");
      return;
    }

    rejectWithdrawal({
      variables: {
        withdrawalRequestId: parseInt(withdrawalRequestId || "0"),
        rejectReason,
      },
    });
  };

  const handleMarkPaid = () => {
    if (!externalTransferRef.trim()) {
      alert("외부 이체 참조 번호를 입력해주세요.");
      return;
    }

    markWithdrawalPaid({
      variables: {
        withdrawalRequestId: parseInt(withdrawalRequestId || "0"),
        externalTransferRef,
      },
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatAmount = (amount: number) => {
    return amount.toLocaleString() + "원";
  };

  if (loading) return <div className="text-center py-8">로딩 중...</div>;
  if (error)
    return (
      <div className="text-center py-8 text-red-600">에러: {error.message}</div>
    );

  const withdrawal = data?.missionAdminWithdrawal;
  if (!withdrawal) {
    return (
      <div className="text-center py-8">출금 요청 정보를 찾을 수 없습니다.</div>
    );
  }

  const wallet = withdrawal.wallet;
  const ledgers = ledgersData?.userWalletLedgers || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate("/ops/mission/reviews/withdrawals")}
          className="text-blue-600 hover:text-blue-700"
        >
          ← 목록으로
        </button>
        <h1 className="text-2xl font-bold">출금 요청 검수</h1>
        <div></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 요청 정보 */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">요청 정보</h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-500">
                  유저
                </label>
                <p className="text-base">
                  {withdrawal.user.name || `유저 ${withdrawal.user.id}`} (ID:{" "}
                  {withdrawal.user.id})
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">
                  요청 금액
                </label>
                <p className="text-2xl font-bold text-blue-600">
                  {formatAmount(withdrawal.amountRequested)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">
                  계좌 정보
                </label>
                <div className="mt-1 space-y-1">
                  <p className="text-base">
                    {withdrawal.bankName} {withdrawal.accountNumber || withdrawal.accountNumberMasked}
                  </p>
                  <p className="text-sm text-gray-500">
                    예금주: {withdrawal.accountHolder}
                  </p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">
                  요청 일시
                </label>
                <p className="text-base">
                  {withdrawal.requestedAt
                    ? formatDate(withdrawal.requestedAt)
                    : "-"}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">
                  상태
                </label>
                <p className="text-base">
                  {withdrawal.status === "REQUESTED" && (
                    <span className="text-yellow-600 font-medium">대기 중</span>
                  )}
                  {withdrawal.status === "APPROVED" && (
                    <span className="text-blue-600 font-medium">승인됨</span>
                  )}
                  {withdrawal.status === "REJECTED" && (
                    <span className="text-red-600 font-medium">반려됨</span>
                  )}
                  {withdrawal.status === "PAID" && (
                    <span className="text-green-600 font-medium">
                      이체 완료
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* 유저 지갑 정보 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">유저 지갑 정보</h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-500">
                  현재 잔액
                </label>
                <p className="text-xl font-bold text-green-600">
                  {formatAmount(wallet.availableBalance)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">
                  홀드 중인 금액
                </label>
                <p className="text-xl font-bold text-yellow-600">
                  {formatAmount(wallet.heldBalance)}
                </p>
              </div>
              <div className="pt-3 border-t border-gray-200">
                <label className="text-sm font-medium text-gray-500">
                  잔액 확인
                </label>
                <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">요청 금액</span>
                    <span className="text-base font-medium">
                      {formatAmount(withdrawal.amountRequested)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-sm text-gray-600">
                      홀드 중인 금액
                    </span>
                    <span className="text-base font-medium">
                      {formatAmount(wallet.heldBalance)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-200">
                    <span className="text-sm font-medium">검증 결과</span>
                    <span
                      className={`text-base font-bold ${
                        wallet.heldBalance >= withdrawal.amountRequested
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {wallet.heldBalance >= withdrawal.amountRequested
                        ? "정상"
                        : "잔액 부족"}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    출금 요청 생성 시 이미 사용 가능 잔액에서 홀드로 이동했습니다.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 최근 거래 내역 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">최근 거래 내역</h2>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {ledgers.slice(0, 10).map((ledger: any) => (
                <div
                  key={ledger.id}
                  className="flex justify-between items-center p-2 border-b border-gray-100"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {ledger.memo || ledger.type}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDate(ledger.createdAt)}
                    </p>
                  </div>
                  <div
                    className={`text-sm font-medium ${
                      ledger.direction === "IN"
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {ledger.direction === "IN" ? "+" : "-"}
                    {formatAmount(ledger.amount)}
                  </div>
                </div>
              ))}
              {ledgers.length === 0 && (
                <p className="text-center text-gray-500 py-4">
                  거래 내역이 없습니다.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* 검수 액션 */}
        <div className="space-y-6">
          {withdrawal.status === "REQUESTED" && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">검수</h2>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <button
                    onClick={handleApprove}
                    disabled={approving}
                    className="flex-1 bg-green-600 text-white rounded-lg py-3 font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {approving ? "처리 중..." : "승인"}
                  </button>
                  <button
                    onClick={() => setShowRejectModal(true)}
                    disabled={rejecting}
                    className="flex-1 bg-red-600 text-white rounded-lg py-3 font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    반려
                  </button>
                </div>
              </div>
            </div>
          )}

          {withdrawal.status === "APPROVED" && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">이체 완료 처리</h2>
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  실제 계좌 이체를 완료한 후, 이체 완료 처리를 해주세요.
                </p>
                <button
                  onClick={() => setShowPaymentModal(true)}
                  className="w-full bg-blue-600 text-white rounded-lg py-3 font-semibold hover:bg-blue-700"
                >
                  이체 완료 처리
                </button>
              </div>
            </div>
          )}

          {/* 검수 완료 상태 */}
          {withdrawal.status !== "REQUESTED" &&
            withdrawal.status !== "APPROVED" && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-4">검수 상태</h2>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      상태
                    </label>
                    <p className="text-base">
                      {withdrawal.status === "REJECTED" && (
                        <span className="text-red-600 font-medium">반려됨</span>
                      )}
                      {withdrawal.status === "PAID" && (
                        <span className="text-green-600 font-medium">
                          이체 완료
                        </span>
                      )}
                    </p>
                  </div>
                  {withdrawal.reviewedAt && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        검수 일시
                      </label>
                      <p className="text-base">
                        {formatDate(withdrawal.reviewedAt)}
                      </p>
                    </div>
                  )}
                  {withdrawal.paidAt && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        이체 완료 일시
                      </label>
                      <p className="text-base">
                        {formatDate(withdrawal.paidAt)}
                      </p>
                    </div>
                  )}
                  {withdrawal.rejectReason && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        반려 사유
                      </label>
                      <p className="text-base bg-gray-50 p-3 rounded">
                        {withdrawal.rejectReason}
                      </p>
                    </div>
                  )}
                  {withdrawal.externalTransferRef && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        외부 이체 참조 번호
                      </label>
                      <p className="text-base bg-gray-50 p-3 rounded">
                        {withdrawal.externalTransferRef}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
        </div>
      </div>

      {/* 반려 모달 */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">반려 사유 입력</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4"
              rows={4}
              placeholder="반려 사유를 입력하세요"
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason("");
                }}
                className="flex-1 border border-gray-300 rounded-lg py-2 font-semibold text-gray-700"
              >
                취소
              </button>
              <button
                onClick={handleReject}
                disabled={rejecting || !rejectReason.trim()}
                className="flex-1 bg-red-600 text-white rounded-lg py-2 font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {rejecting ? "처리 중..." : "반려"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 이체 완료 모달 */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">이체 완료 처리</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                외부 이체 참조 번호
              </label>
              <input
                type="text"
                value={externalTransferRef}
                onChange={(e) => setExternalTransferRef(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="이체 참조 번호를 입력하세요"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setExternalTransferRef("");
                }}
                className="flex-1 border border-gray-300 rounded-lg py-2 font-semibold text-gray-700"
              >
                취소
              </button>
              <button
                onClick={handleMarkPaid}
                disabled={markingPaid || !externalTransferRef.trim()}
                className="flex-1 bg-blue-600 text-white rounded-lg py-2 font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {markingPaid ? "처리 중..." : "완료"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawalDetail;
